'use client';

import { useMemo } from 'react';
import { useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';

/**
 * @description Hook to get job search filters based on current sidebar selection.
 * Returns filters for the selected job alert, keyword search, or undefined for "new search" (default jobs).
 */
export const useJobSearchFiltersFromSidebar = () => {
  const { currentView } = useSidebarNavigation();

  const filters = useMemo(() => {
    if (currentView === 'new-search' || currentView === 'my-bestie-landing' || currentView === 'browse-all') {
      return undefined;
    }

    if (currentView === 'new-job-search-form' || (typeof currentView === 'object' && 'editSearch' in currentView)) {
      return undefined;
    }

    // Keyword search (from last experience or next job)
    if (typeof currentView === 'object' && 'keyword' in currentView) {
      return { keyword: currentView.keyword };
    }

    // Candidate search (includes alert clicks — alerts are converted to candidateSearch on click)
    if (typeof currentView === 'object' && 'candidateSearch' in currentView) {
      const { keyword, locations } = currentView.candidateSearch;
      const urlParams: Record<string, string | string[] | number[]> = {};
      if (keyword) urlParams.keyword = keyword;
      if (locations?.length) urlParams.location = locations;
      return urlParams;
    }

    return undefined;
  }, [currentView]);

  return {
    filters,
    isNewSearch: currentView === 'new-search',
    isMyBestieLanding: currentView === 'my-bestie-landing',
    isJobSearchForm: currentView === 'new-job-search-form' || (typeof currentView === 'object' && 'editSearch' in currentView),
  };
};
